/**
 * Hook para verificar e gerenciar transferências pendentes de uma conversa
 * 
 * Retorna se existe transferência pendente para o usuário atual
 * e funções para aceitar (iniciar atendimento) a transferência.
 * 
 * Usado para controlar o fluxo de atendimento:
 * - Transferência pendente = input bloqueado, botão "Iniciar" visível
 * - Após aceitar = conversa move para "Em Atendimento"
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger para debug do hook
const logger = createLogger('usePendingTransfer');

// Interface para transferência pendente
interface PendingTransfer {
  id: string;
  conversation_id: string;
  from_agent_id: string | null;
  to_agent_id: string | null;
  status: string;
  created_at: string;
}

interface UsePendingTransferReturn {
  /** Indica se existe uma transferência pendente para o usuário atual */
  hasPendingTransfer: boolean;
  /** Dados da transferência pendente (se existir) */
  pendingTransfer: PendingTransfer | null;
  /** Indica se está carregando dados */
  loading: boolean;
  /** Indica se está processando a aceitação */
  accepting: boolean;
  /** Aceita a transferência e inicia o atendimento */
  acceptTransferAndStart: () => Promise<boolean>;
  /** Recarrega os dados da transferência */
  refetch: () => Promise<void>;
}

/**
 * Hook para gerenciar transferências pendentes de uma conversa
 * 
 * @param conversationId - ID da conversa para verificar transferências
 * @returns Objeto com estado e funções para gerenciar transferência
 */
export function usePendingTransfer(conversationId: string | null): UsePendingTransferReturn {
  const [pendingTransfer, setPendingTransfer] = useState<PendingTransfer | null>(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const { user } = useAuth();

  /**
   * Busca transferência pendente para a conversa atual
   * Status = 'pending' AND to_agent_id = usuário atual
   */
  const fetchPendingTransfer = useCallback(async () => {
    if (!conversationId || !user?.id) {
      setPendingTransfer(null);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('conversation_transfers')
        .select('id, conversation_id, from_agent_id, to_agent_id, status, created_at')
        .eq('conversation_id', conversationId)
        .eq('to_agent_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error('Erro ao buscar transferência pendente:', error);
        setPendingTransfer(null);
        return;
      }

      setPendingTransfer(data);
      logger.debug('Transferência pendente:', data ? 'encontrada' : 'não encontrada');
    } catch (error) {
      logger.error('Erro ao buscar transferência pendente:', error);
      setPendingTransfer(null);
    } finally {
      setLoading(false);
    }
  }, [conversationId, user?.id]);

  // Busca transferência ao montar ou quando conversationId/user mudar
  useEffect(() => {
    fetchPendingTransfer();
  }, [fetchPendingTransfer]);

  // Sufixo único e estável por instância do hook.
  // Evita o erro "cannot add postgres_changes callbacks ... after subscribe()"
  // quando o efeito remonta rapidamente (StrictMode / reabertura do modal de chat
  // sobreposto ao modal de negócio) e o supabase-js reaproveita um canal ainda
  // em estado joining/joined para o mesmo nome. Mesmo padrão usado em useDealCadence.
  const channelUidRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  // Configura subscription realtime para atualizações de transferência
  useEffect(() => {
    if (!conversationId || !user?.id) return;

    // Nome único por instância + conversa: garante canal novo a cada montagem
    const channelName = `transfer-${conversationId}-${channelUidRef.current}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_transfers',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          // Recarrega quando há mudanças na transferência
          fetchPendingTransfer();
        }
      )
      .subscribe();

    // Guarda contra cleanup duplo (StrictMode) - removeChannel idempotente
    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      supabase.removeChannel(channel);
    };
  }, [conversationId, user?.id, fetchPendingTransfer]);

  /**
   * Aceita a transferência e atualiza status da conversa para 'open'
   * Move a conversa de "Transferências" para "Em Atendimento"
   */
  const acceptTransferAndStart = useCallback(async (): Promise<boolean> => {
    if (!pendingTransfer || !user?.id) {
      logger.warn('Tentativa de aceitar sem transferência pendente');
      return false;
    }

    setAccepting(true);
    try {
      logger.info('Aceitando transferência:', pendingTransfer.id);

      // 1. Atualiza transfer para 'accepted'
      const { error: transferError } = await supabase
        .from('conversation_transfers')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString(),
        })
        .eq('id', pendingTransfer.id);

      if (transferError) {
        logger.error('Erro ao atualizar transferência:', transferError);
        toast.error('Erro ao iniciar atendimento');
        return false;
      }

      // 2. Atualiza conversa para 'open' e PAUSA O BOT automaticamente
      // Quando agente inicia atendimento, bot deve ser pausado para evitar interferência
      const { error: conversationError } = await supabase
        .from('conversations')
        .update({ 
          status: 'open',
          bot_paused: true,
          bot_paused_at: new Date().toISOString(),
          bot_paused_by: user.id,
        })
        .eq('id', pendingTransfer.conversation_id);

      if (conversationError) {
        logger.error('Erro ao atualizar conversa:', conversationError);
        toast.error('Erro ao iniciar atendimento');
        return false;
      }

      // Limpa estado local
      setPendingTransfer(null);
      logger.info('Transferência aceita com sucesso');
      return true;
    } catch (error) {
      logger.error('Erro ao aceitar transferência:', error);
      toast.error('Erro ao iniciar atendimento');
      return false;
    } finally {
      setAccepting(false);
    }
  }, [pendingTransfer, user?.id]);

  return {
    hasPendingTransfer: !!pendingTransfer,
    pendingTransfer,
    loading,
    accepting,
    acceptTransferAndStart,
    refetch: fetchPendingTransfer,
  };
}
